<template>

  <v-container fluid class="page-content shipper-location-list-table">

    <v-row class="mb-4">
      <v-col class="pt-2">
        <div class="page-title">
          <v-icon class="pr-1">mdi-truck</v-icon>
          Shipper Locations
        </div>
      </v-col>
    </v-row>

    <v-data-table
        :headers="tableHeaders"
        :items="shipperLocItems"
        :search="search"
        :options.sync="dataOptions"
        :footer-props="paginationOptions"
        :loading="isDataLoading"
        :server-items-length="totalItems"
        @click:row="goToItemPage">

      <!-- shipper name -->
      <template v-slot:[`item.name`]="{ item }">
        <span style="font-size:1.14em">
          <router-link :to="generateShipperPageLink(item)">
            {{ item.name }}
          </router-link>
        </span>
      </template>

      <!-- requires appt -->
      <template v-slot:item.requires_appointment="{ item }">
        <span>{{ item.requires_appointment ? 'Yes' : 'No' }}</span>
      </template>

      <!-- last approved date -->
      <template v-slot:item.approved_licenses="{ item }">
        <span>{{ lastApprovedDate(item) }}</span>
      </template>

      <!-- license expires date -->
      <template v-slot:item.model="{ item }">
        <span>{{ licenseExpiresDate(item) }}</span>
      </template>

      <!-- created by -->
      <template v-slot:item.created_by="{ item }">
        <span v-if="item.created_by !== 'routeleaf-api'">Admin</span>
        <span v-else>{{ item.created_by }}</span>
      </template>

      <!-- status -->
      <template v-slot:[`item.shipper_status`]="{ item }">
        <v-chip label outlined :class="item.shipper_status" v-if="item.shipper_status">
          {{ item.shipper_status }}
        </v-chip>
      </template>
    </v-data-table>

  </v-container>

</template>

<script>
export default {
  name: 'ShipperLocationListDataTable',

  data () {
    return {
      isDataLoading: false,
      shipperLocData: null,
      dataLoadingError: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['name'],
        sortDesc: [false]
      },
      search: null
    }
  },

  computed: {
    tableHeaders () {
      return [
        { text: 'Name', value: 'name' },
        { text: 'Requires Appt?', align: 'center', value: 'requires_appointment' },
        { text: 'Last Approved', sortable: false, value: 'approved_licenses' },
        { text: 'License Expires', sortable: false, value: 'model' },
        { text: 'Status', value: 'shipper_status' }
      ]
    },
    shipperLocItems () {
      return (this.shipperLocData) ? this.shipperLocData.items : []
    },
    dataMeta () {
      return (this.shipperLocData) ? this.shipperLocData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    }
    // Observe changes to the search field
    // search: function (updated) {
    //   this.queryData()
    // }
  },

  methods: {
    // Refresh action to call from parent components
    refresh () {
      this.queryData()
    },

    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMMM D, YYYY') : ''
    },

    // Returns a formatted date for the last approved date
    lastApprovedDate (shipper) {
      if (shipper.approved_licenses && shipper.approved_licenses.length > 0) {
        const sorted = this.$root.utils.orderBy(shipper.approved_licenses, ['approved_at'], ['desc'])
        return this.formatDate(sorted[0].approved_at)
      } else {
        return ''
      }
    },

    // Returns a formatted date for license expiration (per the ship-to address)
    licenseExpiresDate (shipper) {
      if (shipper.approved_licenses && shipper.approved_licenses.length > 0) {
        const sorted = this.$root.utils.orderBy(shipper.approved_licenses, ['approved_at'], ['desc'])
        const licenseType = sorted[0].license_for_shipping_address
        return this.formatDate(sorted[0][`${licenseType}_expiration`])
      } else {
        return ''
      }
    },

    // Generate link data for shipper location page (for a provided shipper item)
    generateShipperPageLink (item) {
      if (item && item.model) {
        return {
          name: 'shipper-item',
          params: { shipperId: item.id }
        }
      } else {
        return ''
      }
    },

    // Navigate to shipper location page (for a provided shipper item)
    goToItemPage (item) {
      if (item && item.model) {
        this.$root.goTo('shipper-item', { shipperId: item.id })
      } else {
        console.error('Invalid item data. Cannot invoke item page.')
      }
    },

    // Perform query of invites
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          with: 'approved_licenses'
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply search query (for name only)
        if (this.search) params['name.contains'] = this.search

        // Retrieve results
        this.shipperLocData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'shippers',
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .shipper-location-list-table .theme--dark.v-data-table {
    /* cursor: pointer; */
  }

  .shipper-location-list-table .status-tag {
    text-transform: uppercase !important;
  }

  .shipper-location-list-table .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .shipper-location-list-table .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .shipper-location-list-table .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 65px;
  }

  .shipper-location-list-table .v-data-table a {
    text-decoration: none;
  }
  .shipper-location-list-table .v-data-table a:hover {
    text-decoration: underline;
  }
</style>
